import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GoogleLogo } from "@phosphor-icons/react";
import { getPostLoginRoute } from "@/utils/postLoginRoute";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Turn any API failure into a message specific enough to act on.
function describeAuthError(err) {
  const resp = err.response;
  if (!resp) return "Can't reach the server. Check your connection and try again.";
  const detail = resp.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) {
    // FastAPI validation errors: [{loc: [..., field], msg}]
    return detail
      .map((d) => `${(d.loc || []).slice(-1)[0] || "input"}: ${d.msg}`)
      .join(" · ");
  }
  return `Server error (${resp.status}). If this keeps happening, the server logs will say why.`;
}

const OAUTH_ERRORS = {
  google_denied: "Google sign-in was cancelled. Try again or use email.",
  google_state: "That sign-in link expired. Please try again.",
  google_failed: "Google sign-in failed. Try again or use email.",
  google_email: "We couldn't verify your Google email. Try email sign-in instead.",
  signup_blocked: "Free account limit reached for this network. Contact support if this seems wrong.",
};

export default function LoginPage() {
  const { user, login, register, api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState("login"); // "login" | "signup" | "forgot"
  const [notice, setNotice] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(OAUTH_ERRORS[searchParams.get("error")] || "");
  const [submitting, setSubmitting] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(getPostLoginRoute(user), { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    api.get("/auth/providers")
      .then((resp) => setGoogleEnabled(!!resp.data?.google))
      .catch(() => setGoogleEnabled(false));
  }, [api]);

  if (user) {
    return null;
  }

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE}/auth/google/login`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      if (mode === "forgot") {
        const resp = await api.post("/auth/forgot-password", { email });
        setNotice(resp.data?.message || "If that email has an account, a reset link is on its way.");
        return;
      }
      const userData = mode === "signup"
        ? await register(name, email, password)
        : await login(email, password);
      navigate(getPostLoginRoute(userData), { replace: true });
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/29791459/pexels-photo-29791459.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Abstract architecture"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--bg-base)] via-[var(--bg-base)]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[var(--text-primary)] tracking-tight leading-none mb-1">
            <img src="/web_logo.svg" alt="Quibn" className="h-14 w-auto" />
          <span className="text-[10px] text-[var(--text-muted)] mb-3 block">Pronounced "KWIB-in"</span>
          </h1>
          <p className="text-[var(--text-secondary)] text-lg leading-relaxed max-w-md">
            Strategic content generation powered by your voice, your rules, your AI.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8">
            <h1 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[var(--text-primary)] tracking-tight"><img src="/web_logo.svg" alt="Quibn" className="h-10 w-auto" /></h1>
            <span className="text-[10px] text-[var(--text-muted)] block mt-0.5">Pronounced "KWIB-in"</span>
            <p className="text-[var(--text-secondary)] mt-2">Strategic content, on-brand, every time.</p>
          </div>

          <div className="bg-[var(--bg-card)]/50 backdrop-blur-xl border border-[var(--border-strong)] rounded-xl p-8">
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[var(--text-primary)] mb-2">
              {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Welcome back"}
            </h2>
            <p className="text-sm text-[var(--text-muted)] mb-6">
              {mode === "signup"
                ? "Start creating on-brand content in minutes."
                : mode === "forgot"
                ? "Enter your email and we'll send you a reset link."
                : "Sign in to pick up where you left off."}
            </p>

            {error && (
              <div className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400" data-testid="login-error">
                {error}
              </div>
            )}
            {notice && (
              <div className="mb-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-400" data-testid="login-notice">
                {notice}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === "signup" && (
                <Input
                  type="text" value={name} required
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                  className="h-11 bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                  data-testid="signup-name"
                />
              )}
              <Input
                type="email" value={email} required
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                autoComplete="email"
                className="h-11 bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                data-testid="login-email"
              />
              {mode !== "forgot" && (
                <Input
                  type="password" value={password} required
                  minLength={mode === "signup" ? 8 : 1}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "Password (8+ characters)" : "Password"}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  className="h-11 bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                  data-testid="login-password"
                />
              )}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold rounded-md h-12 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(255,68,0,0.4)]"
                data-testid="login-submit"
              >
                {submitting ? "One moment..." : mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
              </Button>
            </form>

            {mode === "login" && (
              <p className="text-center text-xs mt-3">
                <button
                  type="button"
                  onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
                  className="text-[var(--text-muted)] hover:text-[#FF4400] transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </p>
            )}

            {googleEnabled && mode !== "forgot" && (
              <>
                <div className="flex items-center gap-3 my-5">
                  <div className="h-px flex-1 bg-[var(--border)]" />
                  <span className="text-xs text-[var(--text-muted)]">or</span>
                  <div className="h-px flex-1 bg-[var(--border)]" />
                </div>
                <Button
                  type="button"
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="w-full h-12 rounded-md border-[var(--border-strong)] text-[var(--text-primary)] gap-2"
                  data-testid="google-login-button"
                >
                  <GoogleLogo size={20} weight="bold" />
                  Continue with Google
                </Button>
              </>
            )}

            <p className="text-center text-sm text-[var(--text-muted)] mt-6">
              {mode === "signup" ? "Already have an account?" : mode === "forgot" ? "Remembered it?" : "New here?"}{" "}
              <button
                type="button"
                onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); setNotice(""); }}
                className="text-[#FF4400] hover:underline font-medium"
                data-testid="toggle-auth-mode"
              >
                {mode === "login" ? "Create an account" : "Sign in"}
              </button>
            </p>

            <p className="text-center text-xs text-[var(--text-muted)] mt-4">
              By continuing, you agree to our <Link to="/legal/terms" className="underline hover:text-[var(--text-secondary)]">terms</Link> and <Link to="/legal/privacy" className="underline hover:text-[var(--text-secondary)]">privacy policy</Link>.
            </p>

            <p className="text-center text-xs text-[var(--text-muted)] mt-4">
              <button onClick={() => navigate("/")} className="hover:text-[var(--text-secondary)] transition-colors" data-testid="back-to-home">&larr; Back to home</button>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
